import { Controller } from "@hotwired/stimulus";

type SaveEvent = CustomEvent<{ elt: HTMLElement; xhr: XMLHttpRequest; successful?: boolean; failed?: boolean }>;

/** Keep one HTMX write in flight and preserve the editor until its latest text is saved. */
export default class extends Controller<HTMLFormElement> {
  static targets = ["files", "attachmentStatus", "attachmentNames", "uploadRetry"];
  static values = { uploading: String, uploadFailed: String };
  declare readonly filesTarget: HTMLInputElement;
  declare readonly attachmentStatusTarget: HTMLElement;
  declare readonly attachmentNamesTarget: HTMLElement;
  declare readonly uploadRetryTarget: HTMLElement;
  declare readonly uploadingValue: string;
  declare readonly uploadFailedValue: string;
  private uploading = false;
  private uploadFiles: File[] = [];
  private uploadId = "";

  private timer: ReturnType<typeof setTimeout> | undefined;
  private saved = "";
  private submitted = "";
  private inFlight = false;
  private leaving = false;
  private pendingClick: HTMLElement | null = null;

  private outside = (event: Event): void => {
    if (!this.element.classList.contains("hidden") && event.target instanceof Node) {
      if (this.element.contains(event.target)) {
        this.leaving = false;
        this.pendingClick = null;
      } else this.leave();
    }
  };

  private outsideClick = (event: MouseEvent): void => {
    if (this.element.classList.contains("hidden") || !(event.target instanceof Element) || this.element.contains(event.target)) return;
    const action = event.target.closest<HTMLElement>("a, button");
    if (action && (this.uploading || this.uploadFiles.length > 0 || this.inFlight || this.snapshot() !== this.saved)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.pendingClick = action;
      this.leave();
    }
  };

  connect(): void {
    if (!this.element.classList.contains("hidden")) this.saved = this.snapshot();
    document.addEventListener("pointerdown", this.outside);
    document.addEventListener("click", this.outsideClick, true);
  }

  disconnect(): void {
    clearTimeout(this.timer);
    document.removeEventListener("pointerdown", this.outside);
    document.removeEventListener("click", this.outsideClick, true);
  }

  open(): void {
    if (this.element.dataset.streamPreserveSelf !== "true") this.saved = this.snapshot();
    this.leaving = false;
    this.element.dataset.streamPreserveSelf = "true";
  }

  changed(event: Event): void {
    if (event.type === "input" && !(event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLInputElement)) return;
    clearTimeout(this.timer);
    this.timer = undefined;
    if (event.type === "change") this.flush();
    else this.timer = setTimeout(() => { this.timer = undefined; this.flush(); }, 300);
  }

  focusout(event: FocusEvent): void {
    // A dropdown option can briefly clear focus before its click selects it.
    // Pointer exits are handled by pointerdown; this handles keyboard exits.
    if (event.relatedTarget instanceof Node && !this.element.contains(event.relatedTarget)) this.leave();
  }

  leave(): void {
    this.leaving = true;
    clearTimeout(this.timer);
    this.timer = undefined;
    this.flush();
  }

  submit(event: Event): void {
    event.preventDefault();
    this.flush();
  }

  flush(): void {
    if (this.inFlight || this.uploading || this.uploadFiles.length > 0) return;
    for (const field of this.element.querySelectorAll('[data-controller~="email-recipients"]')) {
      field.dispatchEvent(new CustomEvent("email-recipients-commit", { detail: { commitFocused: this.leaving } }));
    }
    if (!this.element.checkValidity()) {
      const invalid = this.element.querySelector<HTMLInputElement>(":invalid");
      this.dispatch("invalid", { detail: { message: invalid?.validationMessage } });
      return;
    }
    if (this.snapshot() === this.saved) {
      if (this.leaving) {
        delete this.element.dataset.streamPreserveSelf;
        this.dispatch("close");
        const action = this.pendingClick;
        this.pendingClick = null;
        if (action?.isConnected) action.click();
      }
      return;
    }
    this.element.dispatchEvent(new Event("email-save", { bubbles: true }));
  }

  started(event: SaveEvent): void {
    if (event.detail.elt !== this.element) return;
    this.inFlight = true;
    this.submitted = this.snapshot();
  }

  finished(event: SaveEvent): void {
    if (event.detail.elt !== this.element) return;
    this.inFlight = false;
    if (!event.detail.successful || event.detail.failed) {
      this.pendingClick = null;
      return;
    }
    const token = this.element.elements.namedItem("emailDraftToken");
    const nextToken = event.detail.xhr.getResponseHeader("X-Email-Draft-Token");
    if (token instanceof HTMLInputElement && nextToken) token.value = nextToken;
    this.saved = this.submitted;
    // Let HTMX release its request lock before sending changes typed meanwhile.
    queueMicrotask(() => { if (this.timer === undefined) this.flush(); });
  }

  selectFiles(event: Event): void {
    event.stopImmediatePropagation();
    this.addFiles(Array.from(this.filesTarget.files || []));
    this.filesTarget.value = "";
  }

  dragOver(event: DragEvent): void {
    if (!event.dataTransfer?.types.includes("Files")) return;
    event.preventDefault();
    event.stopPropagation();
    const blocked = this.uploading || this.uploadFiles.length > 0;
    event.dataTransfer.dropEffect = blocked ? "none" : "copy";
    if (!blocked) this.element.classList.add("is-dragging");
  }

  dragLeave(event: DragEvent): void {
    if (event.relatedTarget instanceof Node && this.element.contains(event.relatedTarget)) return;
    this.element.classList.remove("is-dragging");
  }

  drop(event: DragEvent): void {
    if (!event.dataTransfer?.types.includes("Files")) return;
    event.preventDefault();
    event.stopPropagation();
    this.element.classList.remove("is-dragging");
    this.addFiles(Array.from(event.dataTransfer.files));
  }

  private addFiles(files: File[]): void {
    if (this.uploading || this.uploadFiles.length || !files.length) return;
    this.uploadFiles = files;
    this.uploadId = crypto.randomUUID();
    void this.upload();
  }

  async upload(): Promise<void> {
    if (this.uploading || !this.uploadFiles.length) return;
    this.uploading = true;
    this.filesTarget.disabled = true;
    this.uploadRetryTarget.hidden = true;
    this.attachmentStatusTarget.textContent = this.uploadingValue;
    const payload = new FormData();
    payload.append("mutation", "email-attachments");
    payload.append("uploadId", this.uploadId);
    for (const file of this.uploadFiles) payload.append("attachments", file);
    try {
      const response = await fetch(this.element.getAttribute("hx-patch")!, {
        method: "PATCH", body: payload, headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      if (response.status !== 204) {
        this.pendingClick = null;
        this.leaving = false;
        this.attachmentStatusTarget.textContent = response.status === 422 ? await response.text() : this.uploadFailedValue;
        this.uploadRetryTarget.hidden = false;
        return;
      }
      for (const file of this.uploadFiles) {
        const name = document.createElement("li");
        name.textContent = file.name;
        this.attachmentNamesTarget.append(name);
      }
      this.uploadFiles = [];
      this.attachmentStatusTarget.textContent = "";
    } catch {
      this.pendingClick = null;
      this.leaving = false;
      this.attachmentStatusTarget.textContent = this.uploadFailedValue;
      this.uploadRetryTarget.hidden = false;
    } finally {
      this.uploading = false;
      this.filesTarget.disabled = this.uploadFiles.length > 0;
      this.flush();
    }
  }

  cancelUpload(): void {
    if (this.uploading) return;
    this.uploadFiles = [];
    this.filesTarget.disabled = false;
    this.uploadRetryTarget.hidden = true;
    this.attachmentStatusTarget.textContent = "";
    this.flush();
  }

  private snapshot(): string {
    return JSON.stringify(Array.from(new FormData(this.element).entries()).filter(([name]) => name !== "emailDraftToken" && !name.startsWith("recipientQuery-")));
  }
}
